import { useMemo } from 'react';

export default function useWorkflowLayout(workflow) {
    return useMemo(() => {
        const steps = Array.isArray(workflow?.steps) ? workflow.steps : [];

        const nodeWidth = 500;
        const nodeHeight = 140;
        // increased spacing to provide more distance between steps
        const gapX = 30;
        const gapY = 100;

        // Build adjacency and incoming counts
        const adj = new Map();
        const incoming = new Map();
        steps.forEach(s => {
            adj.set(s.step_id, []);
            incoming.set(s.step_id, 0);
        });

        const edges = [];
        steps.forEach(s => {
            const from = s.step_id;
            const actions = s.actions || {};
            Object.entries(actions).forEach(([key, a]) => {
                if (a && a.next_step !== undefined) {
                    edges.push({ from, to: a.next_step, action: key, label: a.title || a.action_id, conditional: !!a.condition });
                    if (!adj.has(from)) adj.set(from, []);
                    adj.get(from).push(a.next_step);
                    incoming.set(a.next_step, (incoming.get(a.next_step) || 0) + 1);
                }
                if (a && a.previous_step !== undefined) {
                    edges.push({ from: a.previous_step, to: from, label: a.title || a.action_id, conditional: !!a.condition });
                    if (!adj.has(a.previous_step)) adj.set(a.previous_step, []);
                    adj.get(a.previous_step).push(from);
                    incoming.set(from, (incoming.get(from) || 0) + 1);
                }
            });
        });

        // Roots = nodes with 0 incoming; fallback to first step
        let roots = steps.filter(s => (incoming.get(s.step_id) || 0) === 0).map(s => s.step_id);
        if (roots.length === 0 && steps.length) roots = [steps[0].step_id];

        // BFS layers
        const layerById = new Map();
        const visited = new Set();
        const queue = roots.map(r => ({ id: r, layer: 0 }));
        while (queue.length) {
            const { id, layer } = queue.shift();
            if (visited.has(id)) continue;
            visited.add(id);
            layerById.set(id, layer);
            (adj.get(id) || []).forEach(n => {
                if (!visited.has(n)) queue.push({ id: n, layer: layer + 1 });
            });
        }

        // Put any unvisited nodes at next available layer
        const maxLayer = Math.max(0, ...Array.from(layerById.values(), v => v));
        steps.forEach(s => {
            if (!layerById.has(s.step_id)) layerById.set(s.step_id, maxLayer + 1);
        });

        // Group ids by layer
        const layers = new Map();
        Array.from(layerById.entries()).forEach(([id, layer]) => {
            const cur = layers.get(layer) || [];
            cur.push(id);
            layers.set(layer, cur);
        });

        // Create nodes positions
        const nodes = [];
        // Vertical flow: layers go top -> bottom; nodes in a layer are arranged left -> right
        Array.from(layers.entries()).forEach(([layerIndex, ids]) => {
            const totalWidth = ids.length * nodeWidth + Math.max(0, (ids.length - 1)) * gapX;
            const startX = 100; // left offset for the layer
            ids.forEach((id, i) => {
                const x = startX + i * (nodeWidth + gapX);
                const y = layerIndex * (nodeHeight + gapY) + 100;
                const step = steps.find(s => s.step_id === id) || { title: String(id), role: [] };
                nodes.push({ id, title: step.title || String(id), role: step.role || [], x, y, width: nodeWidth, height: nodeHeight, raw: step });
            });
        });

        return { nodes, edges };
    }, [workflow]);
}
